import type { CardReviewAnswer } from '@app/@types/redux/answer';
import { Badge, Col, ListGroup, Row, StarRatings, UncontrolledTooltip } from '@components/UI/Html';
import { i18nCText } from '@libs/i18n/I18n';
import { translate } from '@state/utils/helper';
import { marked } from 'marked';
import { useState } from 'react';
import { BiTrendingDown } from 'react-icons/bi';
import './FeedbackState.scss';
import type { UserAnswerFeedback } from '@app/@types/redux/project';

/**
 * Renders a question with its feedback and a star rating.
 *
 * Displays the question text as HTML, the associated feedback, and a star rating. If the score is zero, a half-star is shown instead of no stars.
 *
 * @param question - The question text to display.
 * @param feedback - Optional feedback text related to the question.
 * @param score - The numeric score for the question; zero is displayed as a half-star.
 * @returns A React element containing the question, feedback, and star rating.
 */
function renderQuestion(question: string, feedback: string | undefined, score: number) {
  return (
    <div className="feedback__question__container py-2 px-0">
      <p className="mb-0 pt-2" key={question}>
        <span
          className="card_title"
          dangerouslySetInnerHTML={{
            __html: i18nCText(question),
          }}
        />
        <p className="cardFeedback__feedback pt-2">{feedback}</p>
      </p>
      <div className="pt-2">
        <StarRatings className="" disabled value={score === 0 ? 0.5 : score} small />
      </div>
    </div>
  );
}
interface FeedbackStateProps {
  competencyQuestions: string[];
  skillQuestions: string[];
  preCheckFailed: boolean;
  feedbackText: string | undefined;
  aiRatingFirst: CardReviewAnswer | undefined;
  revieweeUserAnswerStatistics: UserAnswerFeedback['attributes']['revieweeUserAnswer'] | undefined;
}
const FeedbackState = (props: FeedbackStateProps) => {
  const {
    competencyQuestions,
    skillQuestions,
    preCheckFailed,
    feedbackText,
    aiRatingFirst,
    revieweeUserAnswerStatistics,
  } = props;
  const [activeTab, setActiveTab] = useState('Suggestions to Improve');
  return (
    <div className="feedback-container">
      <div className="feedback-tiles-container">
        <div className="feedback-tiles">
          <h3>{translate('tedoFeedback.feedback')}</h3>
          <p className="scoring-rationale">{translate('tedoFeedback.scoringRationale')}</p>
          <div className="tabs">
            <button
              className={activeTab === 'Suggestions to Improve' ? 'active' : ''}
              onClick={() => {
                setActiveTab('Suggestions to Improve');
              }}
            >
              <span className="card_title">{translate('tedoFeedback.suggestionsToImprove')}</span>
            </button>
            <button
              id="disabledTooltip"
              className={preCheckFailed ? 'disabled' : activeTab === 'Key Skills' ? 'active' : ''}
              onClick={() => {
                if (!preCheckFailed) setActiveTab('Key Skills');
              }}
            >
              <span className="card_title">{translate('tedoFeedback.keySkills')}</span>
            </button>
            <button
              id="disabledTooltip"
              className={preCheckFailed ? 'disabled' : activeTab === 'Competencies' ? 'active' : ''}
              onClick={() => {
                if (!preCheckFailed) setActiveTab('Competencies');
              }}
            >
              <span className="card_title">{translate('tedoFeedback.competencies')}</span>
            </button>
            {preCheckFailed && (
              <UncontrolledTooltip delay={0} placement="bottom" target="disabledTooltip">
                {translate('tedoFeedback.poorQuality')}
              </UncontrolledTooltip>
            )}
          </div>
        </div>
      </div>

      <div className="tab-content">
        {activeTab === 'Key Skills' && (
          <div>
            <Row>
              <Col>
                <p className="card_title card_uppercase">
                  {translate('pageElements.cardFeedback.softSkills')}
                </p>
                <ListGroup flush>
                  {skillQuestions.map((question, index) => {
                    const stats = revieweeUserAnswerStatistics?.attributes.statistics;
                    const score =
                      stats?.scores && stats.scores.length >= index ? stats.scores[index] : 0;
                    const feedback =
                      aiRatingFirst?.feedbacks &&
                      Object.keys(aiRatingFirst.feedbacks).length >= index &&
                      aiRatingFirst.feedbacks[index]?.feedback
                        ? aiRatingFirst.feedbacks[index]?.feedback ?? ''
                        : '';
                    return renderQuestion(question, feedback, score);
                  })}
                </ListGroup>
              </Col>
            </Row>
          </div>
        )}
        {activeTab === 'Competencies' && (
          <div>
            <Row>
              <Col>
                <p className="mb-3 card_title card_uppercase">
                  {translate('pageElements.cardFeedback.competency')}
                </p>
                <ListGroup flush>
                  <div className="">
                    {competencyQuestions.map((question, index) => {
                      const stats = revieweeUserAnswerStatistics?.attributes.statistics;
                      const score =
                        stats?.competencyScores && stats.competencyScores.length >= index
                          ? stats.competencyScores[index]
                          : 0;

                      const feedback =
                        aiRatingFirst?.competencyFeedbacks &&
                        Object.keys(aiRatingFirst.competencyFeedbacks).length >= index &&
                        aiRatingFirst.competencyFeedbacks[index]?.feedback
                          ? aiRatingFirst.competencyFeedbacks[index]?.feedback ?? ''
                          : '';
                      return renderQuestion(question, feedback, score);
                    })}
                  </div>
                </ListGroup>
              </Col>
            </Row>
          </div>
        )}
        {activeTab === 'Suggestions to Improve' && (
          <div>
            <Row>
              <Col>
                <p className="card_title mb-3 card_uppercase">
                  {translate('tedoFeedback.suggestionsToImprove')}
                </p>
                <div className="card_border" />
                <div className="d-flex align-items-center mb-2">
                  {preCheckFailed ? (
                    <Badge color="danger">
                      <BiTrendingDown className="mr-1" />
                      <span>{translate('tedoFeedback.poorQuality')}</span>
                    </Badge>
                  ) : null}
                </div>
                <p
                  dangerouslySetInnerHTML={{
                    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                    __html: preCheckFailed
                      ? translate('pageElements.cardFeedback.poorFeedback')
                      : (marked(feedbackText ?? '') as string),
                  }}
                  className="cardFeedback__feedback"
                />
              </Col>
            </Row>
          </div>
        )}
      </div>
    </div>
  );
};
export default FeedbackState;
